import { useCallback, cloneElement } from 'react';
import PropTypes from 'prop-types';
import {
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { dialogSelector } from '../../../store/selectors/dialogSelector';
import { setDialogVisibility as setGlobalDialog } from '../../../store/modules/dialogs';
import { isArray, isArrayEmpty } from '../../../utility/helpers';
import {
  hasAuthPermissionsCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';

const LOGIN = '/login';
const UNAUTHORIZED = '/unauthorized-access';
const NOT_FOUND = '/not-found';

const ProtectedRoute = ({
  auth,
  accessControl,
  organizationSettings,
  children,
  requiredPermissions = [],
  isProfileRoute = false,
  isFeatureActive = true,
  ...rest
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { dialogs } = useSelector(dialogSelector);
  const isUserAuthenticated = auth && auth.access;
  const accessPermissions = hasAuthPermissionsCheck(
    requiredPermissions,
    accessControl
  );
  const hasAccessPermissions = accessPermissions.some(permission => permission);

  const setDialogVisibility = useCallback(
    data => dispatch(setGlobalDialog(data)),
    [dispatch]
  );

  const handleRedirect = (path, state = null) => {
    return <Navigate to={path} state={state} replace />;
  };

  if (isUserAuthenticated) {
    if (!isFeatureActive) {
      return handleRedirect(NOT_FOUND);
    }

    if (
      isArray(requiredPermissions) &&
      !isArrayEmpty(requiredPermissions) &&
      !hasAccessPermissions
    ) {
      return handleRedirect(UNAUTHORIZED);
    }

    if (
      isProfileRoute &&
      !isUserProfileAccessibleCheck(accessControl, auth.id, +id)
    ) {
      return handleRedirect(UNAUTHORIZED);
    }

    return cloneElement(children, {
      location,
      navigate,
      dispatch,
      auth,
      organizationSettings,
      accessControl,
      dialogs,
      setDialogVisibility,
      ...rest,
    });
  }

  return handleRedirect(LOGIN, { from: location });
};

ProtectedRoute.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  isProfileRoute: PropTypes.bool,
  isFeatureActive: PropTypes.bool,
};

export default ProtectedRoute;
